import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Phone, AlertTriangle, TrendingUp, Smile, Tag, MessageSquare,
  Search, Clock, User, Hash, ThumbsDown
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip
} from 'recharts';
import api from '../utils/api';
import { useToast } from './ToastContainer';
import './Calls.css';
import './AgentProfile.css';

const fmtDur = (ms) => {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const sentimentClass = (s) => {
  if (s === 'positive') return 'positive';
  if (s === 'negative') return 'negative';
  return 'neutral';
};

const AgentProfile = () => {
  const { extension } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callSearch, setCallSearch] = useState('');
  const [callPage, setCallPage] = useState(1);
  const [callPages, setCallPages] = useState(1);

  // Fetch profile
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/calls/agents/${encodeURIComponent(extension)}`);
        if (res.data.success) setProfile(res.data.profile);
      } catch (err) {
        console.error(err);
        showError('Failed to load agent profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [extension]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch this agent's calls (paginated, separate from profile payload)
  const fetchCalls = async (opts = {}) => {
    try {
      setCallsLoading(true);
      const params = {
        agent: extension,
        page: opts.page || callPage,
        limit: 25,
        status: 'analyzed'
      };
      if (callSearch) params.search = callSearch;
      const res = await api.get('/api/calls', { params });
      if (res.data.success) {
        setCalls(res.data.calls || []);
        setCallPages(res.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error(err);
      showError('Failed to load calls');
    } finally {
      setCallsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls({ page: callPage });
  }, [extension, callPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCallPage(1);
    fetchCalls({ page: 1 });
  };

  const sentimentMix = useMemo(() => {
    const s = profile?.sentiment_counts || { positive: 0, negative: 0, neutral: 0 };
    const total = s.positive + s.negative + s.neutral || 1;
    return {
      positive: Math.round((s.positive / total) * 100),
      neutral:  Math.round((s.neutral  / total) * 100),
      negative: Math.round((s.negative / total) * 100),
      counts: s,
      total
    };
  }, [profile]);

  if (loading) {
    return (
      <div className="calls-page">
        <div className="calls-loading">
          <div className="spinner" />
          <p>Loading agent profile…</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="calls-page">
        <button className="agent-back" onClick={() => navigate('/calls')}>
          <ArrowLeft size={14} /> Back to calls
        </button>
        <div className="calls-empty">
          <User size={48} />
          <p>No analyzed calls for extension {extension}.</p>
        </div>
      </div>
    );
  }

  const { kpis } = profile;

  return (
    <div className="calls-page">
      <button className="agent-back" onClick={() => navigate('/calls')}>
        <ArrowLeft size={14} /> Back to calls
      </button>

      <div className="calls-header">
        <div>
          <h1 className="calls-title">{profile.agent_name || 'Unknown agent'}</h1>
          <p className="calls-subtitle">
            <Hash size={12} style={{ verticalAlign: 'middle' }} /> ext {profile.agent_extension}
            {' · '}
            {kpis.total_calls} calls analyzed
            {' · '}
            last call {fmtDate(kpis.last_call_at)}
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="calls-kpis">
        <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="kpi-label"><Phone size={14} /> Total calls</div>
          <div className="kpi-value">{kpis.total_calls.toLocaleString()}</div>
        </motion.div>
        <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="kpi-label"><AlertTriangle size={14} /> Urgent</div>
          <div className="kpi-value negative">{kpis.urgent_count}</div>
        </motion.div>
        <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="kpi-label"><TrendingUp size={14} /> Avg sentiment</div>
          <div className={`kpi-value ${kpis.avg_sentiment_score == null ? '' : kpis.avg_sentiment_score >= 0.6 ? 'positive' : kpis.avg_sentiment_score >= 0.4 ? 'neutral' : 'negative'}`}>
            {kpis.avg_sentiment_score != null ? kpis.avg_sentiment_score.toFixed(2) : '—'}
          </div>
        </motion.div>
        <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <div className="kpi-label"><Clock size={14} /> Avg call (talking)</div>
          <div className="kpi-value">{fmtDur(kpis.avg_talking_ms)}</div>
        </motion.div>
      </div>

      {/* Row: mix + trend chart */}
      <div className="agent-row">
        {/* Sentiment mix card */}
        <div className="agent-panel">
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <Smile size={16} color="#34d399" /> Sentiment mix
            </div>
            <div className="agent-panel-count">{sentimentMix.total} calls</div>
          </div>
          <div className="agent-mix-bar big">
            <div className="agent-mix-seg positive" style={{ width: `${sentimentMix.positive}%` }} />
            <div className="agent-mix-seg neutral"  style={{ width: `${sentimentMix.neutral}%` }} />
            <div className="agent-mix-seg negative" style={{ width: `${sentimentMix.negative}%` }} />
          </div>
          <div className="agent-mix-legend">
            <span><b style={{ color: '#10b981' }}>{sentimentMix.positive}%</b> positive ({sentimentMix.counts.positive})</span>
            <span><b style={{ color: '#f59e0b' }}>{sentimentMix.neutral}%</b> neutral ({sentimentMix.counts.neutral})</span>
            <span><b style={{ color: '#ef4444' }}>{sentimentMix.negative}%</b> negative ({sentimentMix.counts.negative})</span>
          </div>

          <div className="agent-top3-row">
            <TopList title="Top 3 emotions"   icon={<Smile size={14} color="#c4b5fd" />} items={profile.top_emotions} />
            <TopList title="Top 3 categories" icon={<Tag size={14} color="#60a5fa" />}   items={profile.top_categories} />
          </div>
        </div>

        {/* Daily trend */}
        <div className="agent-panel">
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <TrendingUp size={16} color="#60a5fa" /> Daily calls · last 30 days
            </div>
          </div>
          <div className="agent-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={profile.daily_trend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="agentTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#60a5fa" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false}
                  tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(20,20,25,0.95)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    color: '#fff', fontSize: '0.8rem'
                  }}
                />
                <Area type="monotone" dataKey="total"  stroke="#60a5fa" strokeWidth={2} fill="url(#agentTrendFill)" name="Total" />
                <Area type="monotone" dataKey="urgent" stroke="#ef4444" strokeWidth={2} fill="transparent" name="Urgent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row: negative issues + key phrases + topics */}
      <div className="agent-row-3">
        <div className="agent-panel">
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <ThumbsDown size={16} color="#f87171" /> Negative issues from customers
            </div>
            <div className="agent-panel-count">{profile.top_cons.length}</div>
          </div>
          <ListOrEmpty
            items={profile.top_cons}
            emptyText="No negative issues detected yet."
            color="#f87171"
          />
        </div>

        <div className="agent-panel">
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <MessageSquare size={16} color="#60a5fa" /> Key phrases
            </div>
            <div className="agent-panel-count">{profile.top_phrases.length}</div>
          </div>
          <ListOrEmpty items={profile.top_phrases} emptyText="No phrases extracted." color="#60a5fa" />
        </div>

        <div className="agent-panel">
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <Tag size={16} color="#c4b5fd" /> Topics discussed
            </div>
            <div className="agent-panel-count">{profile.top_topics.length}</div>
          </div>
          <ListOrEmpty items={profile.top_topics} emptyText="No topics yet." color="#c4b5fd" />
        </div>
      </div>

      {/* Urgency samples */}
      {profile.urgency_samples?.length > 0 && (
        <div className="agent-panel" style={{ marginBottom: '1.5rem' }}>
          <div className="agent-panel-head">
            <div className="agent-panel-title">
              <AlertTriangle size={16} color="#ef4444" /> Recent urgency flags
            </div>
            <div className="agent-panel-count">showing {profile.urgency_samples.length}</div>
          </div>
          <div className="agent-urgency-list">
            {profile.urgency_samples.map(u => (
              <Link
                key={u.call_id}
                to={`/calls/${u.call_id}`}
                className="agent-urgency-row"
              >
                <span className="agent-urgency-date">{fmtDate(u.at)}</span>
                <span className="agent-urgency-reason">{u.reason}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Call list for this agent */}
      <div className="agent-panel">
        <div className="agent-panel-head">
          <div className="agent-panel-title">
            <Phone size={16} /> Calls
          </div>
          <form onSubmit={onSearchSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="search-wrap" style={{ width: 240 }}>
              <Search size={14} />
              <input
                value={callSearch}
                onChange={(e) => setCallSearch(e.target.value)}
                placeholder="Search transcripts…"
              />
            </div>
            <button type="submit" className="btn-primary" style={{ padding: '0.4rem 0.9rem' }}>Search</button>
          </form>
        </div>

        {callsLoading ? (
          <div className="calls-loading" style={{ minHeight: 120 }}>
            <div className="spinner" />
          </div>
        ) : calls.length === 0 ? (
          <div className="calls-empty" style={{ padding: '2rem' }}>
            <Phone size={36} />
            <p>No calls found.</p>
          </div>
        ) : (
          <div className="calls-table-container">
            <table className="calls-table">
              <thead>
                <tr>
                  <th>Start</th>
                  <th>Duration</th>
                  <th>Customer</th>
                  <th>Dir</th>
                  <th>Sentiment</th>
                  <th>Emotion</th>
                  <th>Category</th>
                  <th>Urgent</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {calls.map(c => (
                  <tr
                    key={c.id}
                    className="call-row"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/calls/${c.id}`)}
                  >
                    <td>
                      <Link to={`/calls/${c.id}`} className="call-link" onClick={(e) => e.stopPropagation()}>
                        {fmtDate(c.start_time)}
                      </Link>
                    </td>
                    <td>{fmtDur(c.duration_ms)}</td>
                    <td style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: '0.85rem' }}>
                      {c.caller_number || '—'}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{c.direction || '—'}</td>
                    <td>{c.analysis?.sentiment && <span className={`badge ${sentimentClass(c.analysis.sentiment)}`}>{c.analysis.sentiment}</span>}</td>
                    <td>{c.analysis?.detected_emotion || '—'}</td>
                    <td>{c.analysis?.issue_category || '—'}</td>
                    <td>{c.analysis?.urgency_flag ? <AlertTriangle size={16} color="#ef4444" /> : ''}</td>
                    <td className="summary-cell">{c.analysis?.summary_bullets?.[0] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {callPages > 1 && (
          <div className="calls-pagination" style={{ marginTop: '1rem' }}>
            <div className="pagination-center">
              <button disabled={callPage <= 1}         onClick={() => setCallPage(1)}>« First</button>
              <button disabled={callPage <= 1}         onClick={() => setCallPage(p => p - 1)}>‹ Prev</button>
              <span className="page-info">
                Page <strong>{callPage}</strong> of <strong>{callPages}</strong>
              </span>
              <button disabled={callPage >= callPages} onClick={() => setCallPage(p => p + 1)}>Next ›</button>
              <button disabled={callPage >= callPages} onClick={() => setCallPage(callPages)}>Last »</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TopList = ({ title, icon, items }) => (
  <div className="agent-top3">
    <div className="agent-top3-title">{icon} {title}</div>
    {(!items || items.length === 0) ? (
      <div className="agent-top3-empty">—</div>
    ) : (
      <ol className="agent-top3-list">
        {items.map((it, i) => (
          <li key={it.text}>
            <span className="agent-top3-rank">{i + 1}</span>
            <span className="agent-top3-text">{it.text}</span>
            <span className="agent-top3-count">{it.count}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const ListOrEmpty = ({ items, emptyText, color }) => {
  if (!items || items.length === 0) {
    return <div className="agent-list-empty">{emptyText}</div>;
  }
  const max = items[0]?.count || 1;
  return (
    <ul className="agent-list">
      {items.map(it => (
        <li key={it.text}>
          <div className="agent-list-row">
            <span className="agent-list-text">{it.text}</span>
            <span className="agent-list-count">{it.count}</span>
          </div>
          <div className="agent-list-bar" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div style={{ width: `${(it.count / max) * 100}%`, background: color, height: '100%', borderRadius: 999 }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

export default AgentProfile;
